import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/App";
import { useAppMode } from "@/context/AppModeContext";
import { Heart, Search, MessageCircle, Calendar, Coins, Settings, Crown, Wallet, LogOut, ChevronDown, Trophy, Play, LifeBuoy, Clapperboard, PartyPopper } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import PromoVideoDialog from "@/components/PromoVideoDialog";
import MsDLuxeVideosDialog from "@/components/MsDLuxeVideos";
import SupportTicketDialog from "@/components/SupportTicketDialog";
import { useLocale } from "@/i18n/LocaleContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { appName, otherAppName, isDluxe, toggleMode, isPayer } = useAppMode();
  const [showPromo, setShowPromo] = useState(false);
  const [showMsDluxe, setShowMsDluxe] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const { t } = useLocale();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const AppSwitch = ({ small }) => (
    <button
      type="button"
      onClick={toggleMode}
      title={`Switch to ${otherAppName}`}
      className={`flex items-center justify-center rounded-full border transition-transform hover:scale-110 shrink-0 ${
        small ? "w-8 h-8" : "w-9 h-9"
      } ${isDluxe ? "bg-violet-500/15 border-violet-500/40" : "bg-rose-600/15 border-rose-600/40"}`}
      data-testid={small ? "app-switch-btn-mobile" : "app-switch-btn"}
    >
      <Heart className={`w-4 h-4 heart-pulse ${isDluxe ? "text-violet-400 fill-violet-400" : "text-rose-500 fill-rose-500"}`} />
    </button>
  );

  const LogoMenu = ({ small }) => (
    <div className="flex items-center gap-2.5">
      <AppSwitch small={small} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 cursor-pointer outline-none"
            data-testid={small ? "logo-menu-mobile" : "logo-menu"}
          >
            {isDluxe ? (
              <img src="/logo-dluxe.png" alt="DLuxe-meet.com" className={small ? "h-6 w-auto" : "h-8 w-auto"} data-testid="dluxe-logo" />
            ) : (
              <>
                <Heart className={`${small ? "w-6 h-6" : "w-7 h-7"} text-primary fill-primary`} />
                <span className={`${small ? "text-lg" : "text-xl"} font-bold font-['Outfit'] tracking-tight`}>{appName}</span>
              </>
            )}
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuItem onClick={() => navigate("/discover")} data-testid="menu-discover">
            <Search className="w-4 h-4 mr-2" /> Discover
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/events")} data-testid="menu-events">
            <PartyPopper className="w-4 h-4 mr-2" /> {t("nav.events")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
            <Settings className="w-4 h-4 mr-2" /> Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowPromo(true)} data-testid="menu-watch-tour">
            <Play className="w-4 h-4 mr-2" /> {t("nav.watchTour")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowMsDluxe(true)} data-testid="menu-msdluxe-videos">
            <Clapperboard className="w-4 h-4 mr-2" /> {t("nav.msdluxeVideos")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowSupport(true)} data-testid="menu-help-support">
            <LifeBuoy className="w-4 h-4 mr-2" /> {t("nav.helpSupport")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={toggleMode} data-testid="menu-switch-app">
            <Heart className={`w-4 h-4 mr-2 ${isDluxe ? "text-violet-400" : "text-rose-500"}`} /> Switch to {otherAppName}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            className="text-red-400 focus:text-red-400"
            data-testid="menu-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: "/discover", key: "discover", icon: Search, label: t("nav.discover") },
    { path: "/favorites", key: "favorites", icon: Heart, label: t("nav.favorites") },
    { path: "/chat", key: "chat", icon: MessageCircle, label: t("nav.chat") },
    { path: "/premium-offers", key: "offers", icon: Crown, label: t("nav.offers") },
    { path: "/meetups", key: "meetups", icon: Calendar, label: t("nav.meetups") },
    { path: "/leaderboard", key: "top", icon: Trophy, label: t("nav.top") },
    ...(isPayer
      ? [{ path: "/credits", key: "credits", icon: Coins, label: t("nav.credits") }]
      : [{ path: "/earnings", key: "earnings", icon: Wallet, label: t("nav.earnings") }]),
    { path: "/settings", key: "settings", icon: Settings, label: t("nav.settings") }
  ];

  return (
    <>
      <PromoVideoDialog open={showPromo} onOpenChange={setShowPromo} />
      <MsDLuxeVideosDialog open={showMsDluxe} onOpenChange={setShowMsDluxe} />
      <SupportTicketDialog open={showSupport} onOpenChange={setShowSupport} />
      {/* Top Navigation - Desktop */}
      <nav className="fixed top-0 left-0 right-0 z-30 glass hidden md:block">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <LogoMenu />

          <div className="flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                  isActive(item.path)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-white hover:bg-white/5"
                }`}
                data-testid={`nav-${item.key}`}
              >
                <item.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <NotificationBell />
            <button
              onClick={() => navigate("/credits")}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 hover:bg-secondary/20 transition-colors"
              data-testid="credits-chip"
            >
              <Coins className="w-4 h-4 text-secondary" />
              <span className="font-semibold text-secondary">{user?.credits || 0}</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-30 glass md:hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <LogoMenu small />
          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            <NotificationBell testId="notification-bell-btn-mobile" />
            <button
              onClick={() => navigate("/credits")}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary/10 border border-secondary/20"
              data-testid="credits-chip-mobile"
            >
              <Coins className="w-3.5 h-3.5 text-secondary" />
              <span className="text-sm font-semibold text-secondary">{user?.credits || 0}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation - Mobile */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 glass md:hidden">
        <div className="flex items-center justify-between px-1 py-2">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 flex-1 min-w-0 px-0.5 py-2 rounded-xl transition-colors ${
                isActive(item.path)
                  ? "text-primary"
                  : "text-muted-foreground"
              }`}
              data-testid={`mobile-nav-${item.key}`}
            >
              <item.icon className={`w-5 h-5 ${isActive(item.path) ? "fill-primary/20" : ""}`} />
              <span className="text-[10px] truncate max-w-full">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </>
  );
}
